/**
 * As conferências do sistema, UMA POR REGRA REAL do validador — a matéria do
 * marquee da tira de prova.
 *
 * A `chave` é o `id` da regra em `packages/validador` (33 de cadastro em
 * `src/regras/`, 7 de coerência do documento em
 * `src/auditoria/regras-coerencia.ts`), e `conferencias.test.ts` compara os
 * dois conjuntos: regra nova sem rótulo aqui, ou rótulo órfão de regra
 * removida, ficam vermelhos antes de a landing mentir. Foi exatamente assim
 * que o "30 conferências" publicado envelheceu — o validador tinha 40.
 *
 * O `rotulo` é a versão de vitrine: curto, em linguagem de contador, sem
 * jargão de código. Não é a `mensagem` da regra — é o NOME do defeito que
 * ela pega.
 */
/**
 * QUAL motor pega o defeito: o do cadastro (`REGRAS_SOCIEDADE`, roda sobre o
 * dado antes de existir documento) ou o da coerência (`REGRAS_COERENCIA`,
 * roda sobre o texto do .docx). Não é rótulo estético — é o que o ícone do
 * marquee anuncia, e `conferencias.test.ts` confere a divisão contra os dois
 * arrays reais do validador.
 */
export type OndeConfere = 'cadastro' | 'documento';

export type Conferencia = { chave: string; rotulo: string; onde: OndeConfere };

export const CONFERENCIAS: readonly Conferencia[] = [
  // ——— cadastro (REGRAS_SOCIEDADE) ———
  {
    chave: 'pessoa-nao-encontrada',
    rotulo: 'sócio citado sem cadastro',
    onde: 'cadastro',
  },
  { chave: 'cpf-invalido', rotulo: 'CPF inválido', onde: 'cadastro' },
  { chave: 'cnpj-invalido', rotulo: 'CNPJ inválido', onde: 'cadastro' },
  {
    chave: 'socio-sem-capacidade-civil',
    rotulo: 'sócio sem capacidade civil',
    onde: 'cadastro',
  },
  {
    chave: 'menor-socio-capital-nao-integralizado',
    rotulo: 'menor com capital não integralizado',
    onde: 'cadastro',
  },
  {
    chave: 'menor-como-administrador',
    rotulo: 'menor como administrador',
    onde: 'cadastro',
  },
  {
    chave: 'quotas-nao-fecham-capital',
    rotulo: 'quotas que não fecham o capital',
    onde: 'cadastro',
  },
  {
    chave: 'capital-nao-divisivel-pelo-valor-nominal',
    rotulo: 'capital não divisível pelo valor nominal',
    onde: 'cadastro',
  },
  {
    chave: 'integralizacao-acima-do-subscrito',
    rotulo: 'integralização acima do subscrito',
    onde: 'cadastro',
  },
  {
    chave: 'capital-nao-integralizado-sem-prazo',
    rotulo: 'capital a integralizar sem prazo',
    onde: 'cadastro',
  },
  {
    chave: 'filial-com-raiz-diferente',
    rotulo: 'filial com raiz de CNPJ diferente',
    onde: 'cadastro',
  },
  {
    chave: 'filial-encerrada-antes-de-abrir',
    rotulo: 'filial encerrada antes de abrir',
    onde: 'cadastro',
  },
  {
    chave: 'capital-destacado-acima-do-total',
    rotulo: 'capital destacado acima do total',
    onde: 'cadastro',
  },
  {
    chave: 'slu-com-mais-de-um-socio',
    rotulo: 'SLU com mais de um sócio',
    onde: 'cadastro',
  },
  { chave: 'ltda-com-um-socio', rotulo: 'LTDA com um sócio só', onde: 'cadastro' },
  { chave: 'sem-administrador', rotulo: 'sociedade sem administrador', onde: 'cadastro' },
  {
    chave: 'administrador-marcado-socio-sem-quotas',
    rotulo: 'administrador "sócio" sem quotas',
    onde: 'cadastro',
  },
  {
    chave: 'nc-com-socio-pessoa-juridica',
    rotulo: 'nome coletivo com sócio PJ',
    onde: 'cadastro',
  },
  {
    chave: 'cs-sem-comanditado-ou-comanditario',
    rotulo: 'comandita sem as duas classes de sócio',
    onde: 'cadastro',
  },
  {
    chave: 'cs-comanditario-administrando',
    rotulo: 'comanditário administrando',
    onde: 'cadastro',
  },
  {
    chave: 'nc-cs-com-um-socio',
    rotulo: 'comandita ou nome coletivo com um sócio',
    onde: 'cadastro',
  },
  {
    chave: 'administrador-nao-socio-sem-quorum',
    rotulo: 'administrador não sócio sem quórum',
    onde: 'cadastro',
  },
  {
    chave: 'socio-sem-declaracao-desimpedimento',
    rotulo: 'administrador sem declaração de desimpedimento',
    onde: 'cadastro',
  },
  {
    chave: 'enquadramento-incoerente',
    rotulo: 'enquadramento ME/EPP incoerente',
    onde: 'cadastro',
  },
  {
    chave: 'mei-com-contrato-social',
    rotulo: 'MEI com contrato social',
    onde: 'cadastro',
  },
  {
    chave: 'nome-empresarial-sem-designacao',
    rotulo: 'nome empresarial sem a designação do tipo',
    onde: 'cadastro',
  },
  {
    chave: 'sa-com-menos-de-dois-acionistas',
    rotulo: 'S.A. com menos de 2 acionistas',
    onde: 'cadastro',
  },
  {
    chave: 'sa-sem-dez-por-cento-realizado',
    rotulo: 'S.A. sem 10% realizado',
    onde: 'cadastro',
  },
  {
    chave: 'preferenciais-acima-da-metade',
    rotulo: 'preferenciais acima da metade do capital',
    onde: 'cadastro',
  },
  {
    chave: 'preferencial-sem-vantagem',
    rotulo: 'ação preferencial sem vantagem',
    onde: 'cadastro',
  },
  {
    chave: 'aberta-sem-conselho-de-administracao',
    rotulo: 'companhia aberta sem conselho',
    onde: 'cadastro',
  },
  {
    chave: 'capital-autorizado-abaixo-do-subscrito',
    rotulo: 'capital autorizado abaixo do subscrito',
    onde: 'cadastro',
  },
  {
    chave: 'especie-de-acao-em-tipo-sem-acoes',
    rotulo: 'espécie de ação em tipo sem ações',
    onde: 'cadastro',
  },
  // ——— coerência do documento (REGRAS_COERENCIA) ———
  {
    chave: 'nome-divergente',
    rotulo: 'nome divergindo da assinatura',
    onde: 'documento',
  },
  {
    chave: 'data-nascimento-implausivel',
    rotulo: 'sócio nascido depois do ato',
    onde: 'documento',
  },
  {
    chave: 'genero-inconsistente',
    rotulo: 'concordância de gênero trocada',
    onde: 'documento',
  },
  {
    chave: 'clausula-ordinal-repetida',
    rotulo: 'cláusula com numeração repetida',
    onde: 'documento',
  },
  {
    chave: 'logradouro-duplicado',
    rotulo: 'endereço duplicado no texto',
    onde: 'documento',
  },
  {
    chave: 'registro-junta-divergente',
    rotulo: 'o mesmo NIRE em duas datas',
    onde: 'documento',
  },
  {
    chave: 'complemento-divergente',
    rotulo: 'complemento de endereço divergente',
    onde: 'documento',
  },
] as const;
